import { type TrackModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type TracksSortState } from '../../lib/tracks-query-model'
import {
  $jinglesTablePage,
  $jinglesTablePageSize,
  $jinglesTableSelectedRowKeys,
  $jinglesTableSort,
  jinglesTablePageChanged,
  jinglesTablePageReset,
  jinglesTablePageSizeChanged,
  jinglesTableQuery,
  jinglesTableSelectedRowKeysChanged,
  jinglesTableSortChanged,
  jinglesTableSortReset,
} from '../../model/jingles-table.model'
import { useJinglesTableColumns } from './useJinglesTableColumns'

interface JinglesTableProps {
  onEdit: (jingleId: string) => void
}
export const JinglesTable = ({ onEdit }: JinglesTableProps) => {
  const { data, pending } = useUnit(jinglesTableQuery)

  const sort = useUnit($jinglesTableSort)
  const changeSort = useUnit(jinglesTableSortChanged)
  const resetSort = useUnit(jinglesTableSortReset)

  const page = useUnit($jinglesTablePage)
  const changePage = useUnit(jinglesTablePageChanged)
  const resetPage = useUnit(jinglesTablePageReset)
  const pageSize = useUnit($jinglesTablePageSize)
  const changePageSize = useUnit(jinglesTablePageSizeChanged)

  const [selectedRowKeys, changeSelectedRowKeys] = useUnit([
    $jinglesTableSelectedRowKeys,
    jinglesTableSelectedRowKeysChanged,
  ])

  const tableColumns: TableProps<TrackModel>['columns'] =
    useJinglesTableColumns({ onEdit, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<TrackModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as TracksSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  const rowSelection: TableProps<TrackModel>['rowSelection'] = {
    type: 'checkbox',
    selectedRowKeys,
    columnWidth: 50,
    fixed: true,
    onChange: changeSelectedRowKeys,
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        rowSelection={rowSelection}
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Джинглов на странице' },
        }}
      />
    </TableWrapper>
  )
}
